import React, { createContext, useContext, useEffect } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Grid, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { usePostHog } from 'posthog-js/react';
import { AccountData, HolidayCalendar, Language } from '../api';
import { useLanguageChange } from '../orderPage/hooks/useLanguageChange';
import ErrorBoundary from '../ErrorBoundery';
import { LoaderLogoProvider } from '../ui/LoaderLogo';
import { safeStartSessionRecording } from '../utils/safePosthog';

const ManagePassesAccountContext = createContext<AccountData | undefined>(undefined);

export const useManagePassesAccount = () => {
  return useContext(ManagePassesAccountContext);
};

const fontFamily = ['Assistant', 'Roboto', 'sans-serif'].join(',');

const loadFont = () => {
  const fontUrl = 'https://fonts.googleapis.com/css2?family=Assistant:wght@300;400;500;600;700&display=swap';
  if (!document.querySelector(`link[href="${fontUrl}"]`)) {
    const link = document.createElement('link');
    link.href = fontUrl;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }
};

const createManagePassesTheme = (direction: 'ltr' | 'rtl') => {
  // @ts-ignore
  return createTheme({
    direction,
    palette: {
      primary: {
        main: '#71B266',
        contrastText: '#ffffff',
      },
      background: {
        default: '#f5f5f5',
        paper: '#ffffff',
      },
      text: {
        primary: '#1a1a1a',
        secondary: '#666666',
      },
    },
    typography: {
      fontFamily,
      h4: {
        fontWeight: 600,
      },
      h6: {
        fontWeight: 600,
      },
      subtitle1: {
        fontWeight: 500,
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: 8,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 500,
            borderRadius: 8,
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          'html, body, button, input, select, textarea': {
            fontFamily: `${fontFamily} !important`,
          },
          body: {
            WebkitOverflowScrolling: 'touch',
          },
        },
      },
    },
  });
};

type ManagePassesWrapperProps = {
  children: React.ReactNode;
  account?: AccountData;
};

export const ManagePassesWrapper = ({ children, account }: ManagePassesWrapperProps) => {
  const { i18n } = useTranslation();
  const isSmallScreen = useMediaQuery('(max-width:600px)');
  const posthog = usePostHog();

  useLanguageChange(account?.holiday_calendar === HolidayCalendar.ISR ? Language.HE : Language.EN);

  useEffect(() => {
    loadFont();
  }, []);

  useEffect(() => {
    safeStartSessionRecording(posthog);
  }, [posthog]);

  const direction = i18n.dir();
  const theme = createManagePassesTheme(direction);

  return (
    <ErrorBoundary>
      <ManagePassesAccountContext.Provider value={account}>
        <LoaderLogoProvider account={account}>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <Grid
              container
              justifyContent="center"
              sx={{
                minHeight: '100dvh',
                backgroundColor: 'background.default',
                paddingTop: 'env(safe-area-inset-top)',
                paddingBottom: 'env(safe-area-inset-bottom)',
              }}>
              <Grid
                container
                flexDirection="column"
                sx={{
                  width: '100%',
                  maxWidth: isSmallScreen ? '100%' : '540px',
                }}>
                {children}
              </Grid>
            </Grid>
          </ThemeProvider>
        </LoaderLogoProvider>
      </ManagePassesAccountContext.Provider>
    </ErrorBoundary>
  );
};
